import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../images";
import {
  useLogoutMutation,
  useGetUserInfoQuery,
  usePostFileUploadMutation,
  usePostUpdateAvatarMutation,
} from "../../services/apiSlice";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import CommonModal from "../../components/common/Modal/ModalCommon";
import Button from "../../components/common/Button/Button";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [logOut, { isLoading: logoutLoading }] = useLogoutMutation();
  const { data: userInfo, isFetching, refetch } = useGetUserInfoQuery();
  const [postFileUpload, { isLoading: isUploading }] =
    usePostFileUploadMutation();

  const updateAvatarModalRef = useRef();
  const logoutModalRef = useRef();

  const [uploadedImage, setUploadedImage] = useState(null); // Store a single image path
  const [uploadError, setUploadError] = useState(null);
  const [updateAvatar, { isLoading: isLoadingUpdating }] =
    usePostUpdateAvatarMutation();

  const handleImageClick = () => {
    // Create a temporary file input
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "image/*"; // Single file only, no multiple
    fileInput.style.display = "none"; // Hidden

    // Handle file selection
    fileInput.onchange = async (event) => {
      const file = event.target.files[0]; // Take only the first file
      if (!file) return;

      setUploadError(null);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await postFileUpload({ formData }).unwrap();
        if (response?.data) {
          setUploadedImage(response.data); // Replace the single image
          updateAvatarModalRef.current.openModal();
        } else {
          setUploadError(`${t("profile.noPathReturnedFor")} ${file.name}`);
        }
      } catch (error) {
        // console.error("Upload failed for file:", file.name, error);
        setUploadError(
          `${t("profile.uploadFailedFor")} ${file.name}: ${error.message}`
        );
      }

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Append to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const handleLogOut = async () => {
    try {
      const response = await logOut();
      if (
        response.data.message === "Operation successful" ||
        response.data.status === 200
      ) {
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        navigate(`/sign-in`);
      }
    } catch (err) {
      // console.error("Error:", err);
    }
  };

  const handleEditUser = () => {
    navigate(`/profile/edit`);
  };
  const handleTopUp = () => {
    navigate(`/profile/topup`);
  };
  const handleHistoryTopUp = () => {
    navigate(`/profile/historytopup`);
  };
  const handleUpdate = async () => {
    const body = {
      url: uploadedImage,
    };

    try {
      const response = await updateAvatar(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toast.success(t("toast.success.updateAvatarSuccessful"));
        updateAvatarModalRef.current.closeModal();
        refetch();
      }
    } catch (err) {
      // console.error("Error updating company profile:", err);
      toast.error(t("toast.error.failedToUpdateAvatar"));
    }
  };
  return (
    <>
      <LoadingCommon
        statusLoading={
          isFetching || isUploading || isLoadingUpdating || logoutLoading
        }
      />
      <div className="flex flex-col mx-auto w-full bg-neutral-50 max-w-[480px] min-h-screen">
        <Helmet>
          <meta name="theme-color" content="#fafafa" />
        </Helmet>
        <div className="flex flex-col w-full p-3.5">
          <div className="p-4 rounded-xl bg-gradient-to-b from-white to-indigo-100 shadow-lg">
            <div className="flex justify-between items-center py-2 w-full whitespace-nowrap">
              <div className="text-xl font-semibold text-[#3F54DF] truncate">
                {userInfo?.data?.company_name
                  ? userInfo?.data?.company_name
                  : t("base.username")}
              </div>
              <button
                onClick={handleEditUser}
                className="flex items-center px-2.5 py-1.5 text-xs text-black bg-white rounded-md border border-stone-200 min-h-[28px]"
              >
                <img
                  loading="lazy"
                  src={images.EditIcon}
                  className="w-3.5 h-3.5 mr-1"
                  alt="Edit"
                />
                <span>{t("base.edit")}</span>
              </button>
            </div>

            <div className="rounded-xl p-4 w-full max-w-lg mt-4 min-h-[300px]">
              <div className="mt-2">
                {userInfo?.data?.avatar ? (
                  <div className="relative flex justify-center w-full items-center rounded-md px-2 py-1 cursor-pointer">
                    <div className="flex justify-center items-center px-4 py-10 rounded-full">
                      {/* Tooltip Wrapper */}
                      <div className="relative group">
                        <img
                          onClick={handleImageClick}
                          src={`${process.env.REACT_APP_URL_IMAGE}/${userInfo?.data?.avatar?.file_path}`}
                          alt="Uploaded Thumbnail"
                          className="object-cover w-[230px] h-[230px] aspect-square rounded-full"
                        />
                        {/* Tooltip */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                          {t("profile.clickToUpdateAvatar")}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center items-center px-4 py-10">
                    {/* Tooltip Wrapper */}
                    <div className="relative group">
                      <img
                        loading="lazy"
                        src={images.LogoProfile}
                        className="object-cover w-[230px] h-[230px] aspect-square rounded-full"
                        alt="Profile Logo"
                        onClick={handleImageClick}
                      />
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                        {t("profile.clickToUpdateAvatar")}
                      </div>
                    </div>
                  </div>
                )}
                {uploadError && (
                  <p className="text-red-500 text-sm mt-2 w-full">
                    {uploadError}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col p-3 mt-4 w-full bg-white rounded-xl border border-stone-200">
            <div className="flex justify-between items-center w-full text-black">
              <div className="text-sm font-medium">{t("profile.credits")}</div>
              <button
                onClick={handleHistoryTopUp}
                className="flex items-center text-xs text-black"
              >
                <img
                  loading="lazy"
                  src={images.ClockCircleIcon}
                  className="w-4 h-4 mr-1"
                  alt="History"
                />
                <span>{t("base.history")}</span>
              </button>
            </div>
            <div className="flex justify-between items-center mt-3 w-full">
              <div className="flex gap-1 items-center text-3xl font-bold text-blue-700">
                <div>$</div>
                <div>{0}</div>
              </div>
              <button
                onClick={handleTopUp}
                className="flex justify-between items-center px-2.5 py-1.5 text-xs text-black bg-amber-200 rounded min-h-[28px]"
              >
                <img src={images.MoneyIcon} className="w-7 h-3 mr-1" alt="" />
                {t("profile.topUp.title")}
              </button>
            </div>
          </div>
          <button
            // onClick={handleLogOut}
            onClick={() => logoutModalRef.current.openModal()}
            className="w-full max-w-[455px]   px-4 py-3 mt-4 text-sm font-medium text-black bg-white rounded-xl border border-indigo-600"
          >
            {t("base.logout")}
          </button>
        </div>
      </div>
      <CommonModal
        ref={logoutModalRef}
        title={t("profile.logoutConfirmation")}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-4 py-2 rounded-xl bg-white border border-stone-200 text-neutral-500"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.yes")}
              className="flex-1 px-4 py-2 rounded-xl text-white bg-indigo-600 disabled:opacity-50"
              variant="primary"
              onClick={handleLogOut}
              // disabled={isUpdating}
            />
          </>
        )}
      ></CommonModal>
      <CommonModal
        ref={updateAvatarModalRef}
        title={t("profile.areYouSureYouWantToUpdateYourAvatarInformation")}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-4 py-2 rounded-xl bg-white border border-stone-200 text-neutral-500"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.update")}
              className="flex-1 px-4 py-2 rounded-xl text-white bg-[#3F54DF] disabled:opacity-50"
              variant="primary"
              onClick={handleUpdate}
              // disabled={isUpdating}
            />
          </>
        )}
      >
        <div className="flex justify-center items-center px-4  rounded-full">
          <img
            onClick={handleImageClick}
            src={`${process.env.REACT_APP_URL_IMAGE}/${uploadedImage}`}
            alt="Uploaded Thumbnail"
            className="object-cover max-w-[180px] aspect-square rounded-full"
          />
        </div>
      </CommonModal>
    </>
  );
}

export default Profile;
